import React, { createContext, useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { useFirebase } from './FirebaseContext';
import {
  getConceptGateAccount,
  updateConceptGateAccount,
  getConceptGateSettings,
  updateConceptGateSettings,
  resetConceptGateSettings,
  getConceptGateTrade,
  saveConceptGateTrade,
  deleteConceptGateTrade,
  validateConceptGateTrade,
  updateUserRuleConfig,
  testUserRule,
  getUserRules,
  updateUserRuleEnabled as apiUpdateUserRuleEnabled,
  deleteUserRule as apiDeleteUserRule,
  getConceptGateUsageSummary,
  getSubscriptionStatus,
  getValidation,
  // Pipeline API
  getPipelines as apiGetPipelines,
  getActivePipeline as apiGetActivePipeline,
  createPipeline as apiCreatePipeline,
  updatePipeline as apiUpdatePipeline,
  setActivePipeline as apiSetActivePipeline,
  deletePipeline as apiDeletePipeline,
  duplicatePipeline as apiDuplicatePipeline,
  getPipelineRules as apiGetPipelineRules,
} from '../api/conceptgate';
import { getStoredRiskPercent } from '../components/CopilotChatView/ConceptGatePanel/tradeDefaults';
import { getBaseApiUrl } from '../api/core';
import { ENDPOINTS } from '../api/endpoints';
import logger from '../utils/logger';
import { isCodexUi } from '../utils/codexEnv';

const PANEL_WIDTH_KEY = 'ntb:conceptgate:panelWidth';
const PANEL_VISIBLE_KEY = 'ntb:conceptgate:panelVisible';
// Essential rules are now handled by the backend during rule fetching

const ConceptGateContext = createContext();

export const ConceptGateProvider = ({ children }) => {
  const { auth } = useFirebase();

  // Account state
  const [account, setAccount] = useState({
    equity: 100000,
    currency: 'USD',
  });

  // Settings state
  const [settings, setSettings] = useState(null);

  // Usage stats state
  const [usageStats, setUsageStats] = useState({
    totalCost: 0,
    totalTokens: 0,
    validationCount: 0
  });

  // Subscription/tier status
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    staticUsed: 0,
    staticLimit: 5,
    aiUsed: 0,
    aiLimit: 0,
    canValidateStatic: true,
    canValidateAi: false,
    tierName: 'Free',
    tierId: 'free',
    maxStaticRules: 5,
    maxAiRules: 0,
  });

  // Selected instrument (from main view)
  const [selectedInstrument, setSelectedInstrumentState] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Current trade state
  const [currentTrade, setCurrentTrade] = useState(null);
  // Validation results keyed by instrument
  const [validationResultsMap, setValidationResultsMap] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const autosaveTimeoutRef = useRef(null);

  // UI state
  const [panelVisible, setPanelVisible] = useState(() => {
    const saved = localStorage.getItem(PANEL_VISIBLE_KEY);
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : 280;
  });

  const [editMode, setEditMode] = useState(false);
  // Track which instrument is currently validating (null if not validating)
  const [validatingInstrument, setValidatingInstrument] = useState(null);
  // Streaming state keyed by instrument
  const [streamingGatesMap, setStreamingGatesMap] = useState({});
  const [streamingProgressMap, setStreamingProgressMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Rule config modal state
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedRuleForConfig, setSelectedRuleForConfig] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // User rules state
  const [userRules, setUserRules] = useState([]);

  // Pipeline state
  const [pipelines, setPipelines] = useState([]);
  const [activePipeline, setActivePipeline] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);

  // Rules management modal state
  const [rulesManagementOpen, setRulesManagementOpen] = useState(false);

  // Ref to track the instrument being validated (for async callbacks)
  const validatingInstrumentRef = useRef(null);

  // Undo queue state for rule removal - supports multiple pending deletions
  const [undoQueue, setUndoQueue] = useState([]); // Array of { id, message, removedRule, timeoutId }
  const undoTimeoutRefs = useRef(new Map()); // Map of ruleId -> timeoutId
  const [useStreamingValidation, setUseStreamingValidation] = useState(true); // Enable by default
  const streamReaderRef = useRef(null);
  // Track validation continuing in background after disconnect
  const [backgroundValidationMap, setBackgroundValidationMap] = useState({}); // { instrument: { validationId, startTime } }
  const backgroundPollIntervalRef = useRef(null);

  // ==================== Derived State (instrument-scoped) ====================
  // These derive the "current" values from the maps based on selectedInstrument
  const normalizedSelectedInstrument = useMemo(() => {
    if (!selectedInstrument) return null;
    return selectedInstrument.replace('_', '').toUpperCase();
  }, [selectedInstrument]);

  const lastResult = useMemo(() => {
    if (!normalizedSelectedInstrument) return null;
    return validationResultsMap[normalizedSelectedInstrument] || null;
  }, [normalizedSelectedInstrument, validationResultsMap]);

  const validating = useMemo(() => {
    return validatingInstrument === normalizedSelectedInstrument && validatingInstrument !== null;
  }, [validatingInstrument, normalizedSelectedInstrument]);

  const streamingGates = useMemo(() => {
    if (!normalizedSelectedInstrument) return [];
    return streamingGatesMap[normalizedSelectedInstrument] || [];
  }, [normalizedSelectedInstrument, streamingGatesMap]);

  const streamingProgress = useMemo(() => {
    if (!normalizedSelectedInstrument) return { current: 0, total: 0 };
    return streamingProgressMap[normalizedSelectedInstrument] || { current: 0, total: 0 };
  }, [normalizedSelectedInstrument, streamingProgressMap]);

  // Persist panel visibility
  useEffect(() => {
    localStorage.setItem(PANEL_VISIBLE_KEY, JSON.stringify(panelVisible));
  }, [panelVisible]);

  // Persist panel width
  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, panelWidth.toString());
  }, [panelWidth]);

  // Load initial data
  // Reset state helper
  const resetConceptGateState = useCallback(() => {
    setAccount({ equity: 100000, currency: 'USD' });
    setSettings(null);
    setUsageStats({ totalCost: 0, totalTokens: 0, validationCount: 0 });
    setSubscriptionStatus({
      staticUsed: 0, staticLimit: 5, aiUsed: 0, aiLimit: 0,
      canValidateStatic: true, canValidateAi: false, tierName: 'Free', tierId: 'free',
      maxStaticRules: 5, maxAiRules: 0,
    });
    setCurrentTrade(null);
    setValidationResultsMap({});
    setStreamingGatesMap({});
    setStreamingProgressMap({});
    setIsDirty(false);
    setTestResult(null);
    setUserRules([]);
    // Clear pipeline state
    setPipelines([]);
    setActivePipeline(null);
    setPipelineLoading(false);
    // Clear panel state
    setPanelVisible(true);
    // Clear selection state
    setSelectedInstrumentState(null);
    setSelectedCategory(null);
  }, []);

  // Load initial data
  useEffect(() => {
    if (!auth?.currentUser) {
      setLoading(false);
      // Reset all state on logout
      resetConceptGateState();
      return;
    }

    const loadData = async () => {
      // Reset state for new user start
      resetConceptGateState();

      setLoading(true);
      setError(null);

      try {
        // Load account
        await getConceptGateAccount(auth, (data) => {
          if (data && !data.error) {
            setAccount(data);
          }
        });

        // Load usage stats (last 7 days)
        await getConceptGateUsageSummary(auth, (data) => {
          if (data && !data.error) {
            setUsageStats(data);
          }
        }, 7);

        // Load settings
        await getConceptGateSettings(auth, (data) => {
          if (data && !data.error) {
            setSettings(data);
          }
        });

        // Load subscription status
        await getSubscriptionStatus(auth, (data) => {
          if (data && !data.error) {
            setSubscriptionStatus(data);
          }
        });

        // Load current trade (only if instrument is selected)
        // Trade loading will happen when instrument is selected via setSelectedInstrument
      } catch (err) {
        logger.error('Error loading ConceptGate data:', err);
        setError('Failed to load validation settings');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [auth, auth?.currentUser?.uid, resetConceptGateState]);

  // Load pipelines and trade when auth becomes ready and instrument is already selected
  // This handles the page refresh case where instrument is restored from localStorage
  // but auth wasn't ready when setSelectedInstrument was called
  useEffect(() => {
    if (!auth?.currentUser || !selectedInstrument || loading) return;

    const normalizedInst = selectedInstrument.replace('_', '').toUpperCase();

    // Load pipelines if not already loaded for this instrument
    if (pipelines.length === 0 || activePipeline?.instrument !== normalizedInst) {
      setPipelineLoading(true);
      apiGetPipelines(auth, (result) => {
        if (result && !result.error) {
          setPipelines(result);
          const active = result.find(p => p.isActive || p.active);
          if (active) {
            setActivePipeline(active);
          } else if (result.length > 0) {
            setActivePipeline(result[0]);
          }
        }
        setPipelineLoading(false);
      }, normalizedInst);
    }

    // Only load trade if we don't already have one for this instrument
    if (currentTrade?.instrument === normalizedInst) return;

    getConceptGateTrade(auth, (trade) => {
      if (trade) {
        setCurrentTrade({
          ...trade,
          instrument: trade.instrument || normalizedInst,
        });

        // Restore validation result if available
        if (trade.latestValidation && trade.latestValidation.status) {
          const validation = trade.latestValidation;
          const restoredResult = {
            status: validation.status.toLowerCase(),
            totalDurationMs: validation.totalDurationMs,
            totalTokens: validation.totalTokens,
            estimatedCostUsd: validation.estimatedCostUsd,
            gates: (validation.ruleExecutions || []).map((execution) => {
              let actualValue = null;
              let thresholdValue = null;
              if (execution.detailsJson) {
                try {
                  const details = typeof execution.detailsJson === 'string'
                    ? JSON.parse(execution.detailsJson)
                    : execution.detailsJson;
                  actualValue = details.actualValue || details.actual_value;
                  thresholdValue = details.thresholdValue || details.threshold_value;
                } catch (e) {
                  // Ignore parse error
                }
              }
              return {
                id: execution.id,
                libraryRuleId: execution.libraryRuleId,
                userRuleId: execution.userRuleId,
                ruleName: execution.ruleName,
                rule: execution.ruleName,
                ruleType: execution.ruleType,
                status: execution.status || 'SKIPPED',
                message: execution.message,
                suggestedFix: execution.suggestedFix,
                actualValue,
                thresholdValue,
                detailsJson: execution.detailsJson,
                executionOrder: execution.executionOrder,
                durationMs: execution.durationMs,
              };
            }),
            validationId: validation.id,
            validatedAt: validation.completedAt || validation.startedAt,
          };
          setValidationResultsMap(prev => ({
            ...prev,
            [normalizedInst]: restoredResult
          }));
        }
        setIsDirty(false);
      }
    }, normalizedInst);
  }, [auth, auth?.currentUser?.uid, selectedInstrument, loading, currentTrade?.instrument, pipelines.length, activePipeline?.instrument]);

  // Update account
  const updateAccount = useCallback(async (data) => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    await updateConceptGateAccount(auth, (result) => {
      if (result && !result.error) {
        setAccount(result);
      }
    }, data);
  }, [auth]);

  // Update settings
  const updateSettings = useCallback(async (data) => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    await updateConceptGateSettings(auth, (result) => {
      if (result && !result.error) {
        setSettings(result);
        // Mark as dirty if we have a trade
        if (currentTrade) {
          setIsDirty(true);
        }
      }
    }, data);
  }, [auth, currentTrade]);

  // Reset settings
  const resetSettings = useCallback(async () => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    await resetConceptGateSettings(auth, (result) => {
      if (result && !result.error) {
        setSettings(result);
        if (currentTrade) {
          setIsDirty(true);
        }
      }
    });
  }, [auth, currentTrade]);

  // Refresh subscription status (call after validation)
  const refreshSubscription = useCallback(async () => {
    if (!auth?.currentUser) return;

    await getSubscriptionStatus(auth, (data) => {
      if (data && !data.error) {
        setSubscriptionStatus(data);
      }
    });
  }, [auth]);

  // Helper function for normalization
  const normalizeInstrument = useCallback((instrument) => {
    if (!instrument) return 'EURUSD';
    return instrument.replace('_', '').toUpperCase();
  }, []);

  // Set selected instrument (from main view)
  const setSelectedInstrument = useCallback((instrument, category) => {
    const normalizedInstrument = normalizeInstrument(instrument);
    setSelectedInstrumentState(instrument);
    setSelectedCategory(category);

    // Cancel any active validation when switching instruments (optional but reduces confusion)
    if (streamReaderRef.current && validatingInstrumentRef.current !== normalizedInstrument) {
      streamReaderRef.current.cancel();
      streamReaderRef.current = null;
      setValidatingInstrument(null);
      validatingInstrumentRef.current = null;
    }

    // Load pipelines and trade for the new instrument
    if (instrument && auth?.currentUser) {
      // Load pipelines
      setPipelineLoading(true);
      apiGetPipelines(auth, (result) => {
        if (result && !result.error) {
          setPipelines(result);
          const active = result.find(p => p.isActive || p.active);
          if (active) {
            setActivePipeline(active);
          } else if (result.length > 0) {
            setActivePipeline(result[0]);
          }
        }
        setPipelineLoading(false);
      }, normalizedInstrument);

      // Load trade
      try {
        getConceptGateTrade(auth, (trade) => {
          if (trade) {
            setCurrentTrade({
              // Ensure instrument is set if missing in response
              ...trade,
              instrument: trade.instrument || normalizedInstrument || 'EURUSD',
            });

            // Restore validation result if available - store in the map
            if (trade.latestValidation && trade.latestValidation.status) {
              const validation = trade.latestValidation;
              // Transform backend validation to frontend result format
              const restoredResult = {
                status: validation.status.toLowerCase(), // Ensure lowercase for overall status
                totalDurationMs: validation.totalDurationMs,
                totalTokens: validation.totalTokens,
                estimatedCostUsd: validation.estimatedCostUsd,
                gates: (validation.ruleExecutions || []).map((execution) => {
                  // Parse detailsJson if available
                  let actualValue = null;
                  let thresholdValue = null;
                  if (execution.detailsJson) {
                    try {
                      const details = typeof execution.detailsJson === 'string'
                        ? JSON.parse(execution.detailsJson)
                        : execution.detailsJson;
                      actualValue = details.actualValue || details.actual_value;
                      thresholdValue = details.thresholdValue || details.threshold_value;
                    } catch (e) {
                      // Ignore parse error
                    }
                  }

                  return {
                    id: execution.id,
                    libraryRuleId: execution.libraryRuleId,
                    userRuleId: execution.userRuleId,
                    ruleName: execution.ruleName,
                    rule: execution.ruleName,
                    ruleType: execution.ruleType,
                    // Keep status uppercase for UI matching
                    status: execution.status || 'SKIPPED',
                    message: execution.message,
                    suggestedFix: execution.suggestedFix,
                    actualValue,
                    thresholdValue,
                    detailsJson: execution.detailsJson,
                    executionOrder: execution.executionOrder,
                    durationMs: execution.durationMs,
                  };
                }),
                validationId: validation.id,
                validatedAt: validation.completedAt || validation.startedAt,
              };

              // Store in the instrument-keyed map
              setValidationResultsMap(prev => ({
                ...prev,
                [normalizedInstrument]: restoredResult
              }));
              // Since we just loaded a validated trade from DB, it's not dirty unless user changes it
              setIsDirty(false);
            } else {
              // No validation result for this instrument - leave map entry as-is (or null if none)
              setIsDirty(false);
            }
          } else {
            setCurrentTrade(null);
            setIsDirty(false);
          }
        }, normalizedInstrument || 'EURUSD');
      } catch (err) {
        console.error('Error fetching trade:', err);
        setCurrentTrade(null);
      }
    } else {
      // No instrument selected, clear trade
      setCurrentTrade(null);
      setIsDirty(false);
    }
  }, [auth, normalizeInstrument]);

  // Set trade spec (from chat or manual input)
  const setTradeSpec = useCallback((spec) => {
    if (isCodexUi) return;
    // Use selected instrument if not specified in spec
    const tradeWithInstrument = {
      ...spec,
      instrument: spec.instrument || selectedInstrument || 'EURUSD',
    };
    const normalizedInst = tradeWithInstrument.instrument.replace('_', '').toUpperCase();
    setCurrentTrade(tradeWithInstrument);
    setIsDirty(true);
    // Clear any previous validation result for this instrument since trade changed
    setValidationResultsMap(prev => {
      const newMap = { ...prev };
      delete newMap[normalizedInst];
      return newMap;
    });
  }, [selectedInstrument]);

  // Update trade field
  const updateTradeField = useCallback((field, value) => {
    if (isCodexUi) return;

    // Clear validation result for the current instrument since parameters changed
    const inst = currentTrade?.instrument || selectedInstrument || 'EURUSD';
    const normalizedInst = normalizeInstrument(inst);

    setValidationResultsMap(prev => {
      // Only update if we actually have a result to remove
      if (prev[normalizedInst]) {
        const newMap = { ...prev };
        delete newMap[normalizedInst];
        return newMap;
      }
      return prev;
    });

    setCurrentTrade((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  }, [currentTrade, selectedInstrument, normalizeInstrument]);

  const buildTradeSpec = useCallback((trade) => ({
    instrument: normalizeInstrument(trade.instrument || selectedInstrument || 'EURUSD'),
    direction: trade.direction,
    entryPrice: typeof trade.entryPrice === 'string'
      ? parseFloat(trade.entryPrice)
      : trade.entryPrice,
    stopPrice: typeof trade.stopPrice === 'string'
      ? parseFloat(trade.stopPrice)
      : trade.stopPrice,
    targetPrice: trade.targetPrice
      ? (typeof trade.targetPrice === 'string'
        ? parseFloat(trade.targetPrice)
        : trade.targetPrice)
      : null,
    desiredRr: trade.desiredRr
      ? (typeof trade.desiredRr === 'string'
        ? parseFloat(trade.desiredRr)
        : trade.desiredRr)
      : null,
    riskPercent: trade.riskPercent
      ? (typeof trade.riskPercent === 'string'
        ? parseFloat(trade.riskPercent)
        : trade.riskPercent)
      : null,
    riskDollar: trade.riskDollar
      ? (typeof trade.riskDollar === 'string'
        ? parseFloat(trade.riskDollar)
        : trade.riskDollar)
      : null,
  }), [normalizeInstrument, selectedInstrument]);

  useEffect(() => {
    if (!auth?.currentUser || !isDirty || !currentTrade) {
      return;
    }

    if (!currentTrade.direction || !currentTrade.entryPrice || !currentTrade.stopPrice) {
      return;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      const tradeSpec = buildTradeSpec(currentTrade);
      saveConceptGateTrade(auth, (saveResult) => {
        if (saveResult && !saveResult.error) {
          setCurrentTrade((prev) => ({
            ...prev,
            ...saveResult,
          }));
          setIsDirty(false);
        }
      }, tradeSpec);
    }, 600);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [auth, buildTradeSpec, currentTrade, isDirty]);

  // Validate trade
  const validate = useCallback(async () => {
    if (!auth?.currentUser || !currentTrade) return;
    if (isCodexUi) return;

    // Validate required fields before sending
    if (!currentTrade.direction) {
      setError('Direction is required');
      return;
    }
    if (!currentTrade.entryPrice) {
      setError('Entry price is required');
      return;
    }
    if (!currentTrade.stopPrice) {
      setError('Stop price is required');
      return;
    }

    // Normalize instrument format (EUR_USD -> EURUSD)
    const normalizeInstrumentFn = (instrument) => {
      if (!instrument) return 'EURUSD';
      return instrument.replace('_', '').toUpperCase();
    };

    // Capture the instrument being validated
    const validationInstrument = normalizeInstrumentFn(currentTrade.instrument || selectedInstrument || 'EURUSD');

    // Prepare trade spec with only TradeSpec fields (exclude calculated fields, id, userId, etc.)
    const tradeSpec = {
      instrument: validationInstrument,
      direction: currentTrade.direction,
      entryPrice: typeof currentTrade.entryPrice === 'string'
        ? parseFloat(currentTrade.entryPrice)
        : currentTrade.entryPrice,
      stopPrice: typeof currentTrade.stopPrice === 'string'
        ? parseFloat(currentTrade.stopPrice)
        : currentTrade.stopPrice,
      targetPrice: currentTrade.targetPrice
        ? (typeof currentTrade.targetPrice === 'string'
          ? parseFloat(currentTrade.targetPrice)
          : currentTrade.targetPrice)
        : null,
      desiredRr: currentTrade.desiredRr
        ? (typeof currentTrade.desiredRr === 'string'
          ? parseFloat(currentTrade.desiredRr)
          : currentTrade.desiredRr)
        : null,
      riskPercent: currentTrade.riskPercent
        ? (typeof currentTrade.riskPercent === 'string'
          ? parseFloat(currentTrade.riskPercent)
          : currentTrade.riskPercent)
        : getStoredRiskPercent(),
      riskDollar: currentTrade.riskDollar
        ? (typeof currentTrade.riskDollar === 'string'
          ? parseFloat(currentTrade.riskDollar)
          : currentTrade.riskDollar)
        : null,
      // Include pipeline ID to validate only rules for the active pipeline
      pipelineId: activePipeline?.id || null,
    };

    // Track which instrument is being validated
    setValidatingInstrument(validationInstrument);
    validatingInstrumentRef.current = validationInstrument;

    // Initialize streaming state for this instrument
    setStreamingGatesMap(prev => ({ ...prev, [validationInstrument]: [] }));
    setError(null);

    try {
      await validateConceptGateTrade(auth, (result) => {
        if (result && !result.error) {
          // Transform backend response to frontend format
          const transformedResult = {
            ...result,
            // Convert status from "NO_GO" to "no_go" format
            status: result.status ? result.status.toLowerCase() : 'needs_validation',
            // Transform ruleExecutions to gates format
            gates: (result.ruleExecutions || []).map((execution) => {
              // Parse detailsJson if available to extract actual/threshold values
              let actualValue = null;
              let thresholdValue = null;
              if (execution.detailsJson) {
                try {
                  const details = typeof execution.detailsJson === 'string'
                    ? JSON.parse(execution.detailsJson)
                    : execution.detailsJson;
                  actualValue = details.actualRR || details.actualValue || details.actual || null;
                  thresholdValue = details.minRR || details.thresholdValue || details.threshold || details.min || null;
                } catch (e) {
                  logger.warn('Failed to parse detailsJson:', e);
                }
              }

              return {
                libraryRuleId: execution.libraryRuleId,
                ruleName: execution.ruleName,
                rule: execution.ruleName || execution.libraryRuleId,
                status: execution.status || 'SKIPPED',
                message: execution.message || '',
                suggestedFix: execution.suggestedFix || null,
                actualValue,
                thresholdValue,
                ruleType: execution.ruleType,
                executionOrder: execution.executionOrder,
                detailsJson: execution.detailsJson,
              };
            }),
          };

          // Store result in the instrument-keyed map
          setValidationResultsMap(prev => ({
            ...prev,
            [validationInstrument]: transformedResult
          }));
          // Preserve current trade instead of overwriting with undefined normalizedSpec
          // Only update if normalizedSpec is actually provided
          if (result.normalizedSpec) {
            setCurrentTrade(result.normalizedSpec);
          }
          setIsDirty(false);
          setStreamingGatesMap(prev => ({
            ...prev,
            [validationInstrument]: transformedResult.gates
          }));

          // Refresh usage stats
          getConceptGateUsageSummary(auth, (data) => {
            if (data && !data.error) {
              setUsageStats(data);
            }
          }, 7);
        } else {
          // Store error result in the instrument-keyed map
          setValidationResultsMap(prev => ({
            ...prev,
            [validationInstrument]: {
              id: 'error-' + Date.now(),
              status: 'error',
              gates: [],
              error: result?.error || 'Validation failed'
            }
          }));
        }
        // Clear validating state only if this is still the active validation
        if (validatingInstrumentRef.current === validationInstrument) {
          setValidatingInstrument(null);
          validatingInstrumentRef.current = null;
        }
      }, tradeSpec);
    } catch (err) {
      logger.error('Validation error:', err);
      setError('Validation failed: ' + (err.message || 'Unknown error'));
      if (validatingInstrumentRef.current === validationInstrument) {
        setValidatingInstrument(null);
        validatingInstrumentRef.current = null;
      }
    }
  }, [auth, currentTrade, selectedInstrument, activePipeline?.id]);

  // Poll for validation result when connection is lost
  const startPollingForValidation = useCallback((validationId, instrument) => {
    if (backgroundPollIntervalRef.current) {
      clearInterval(backgroundPollIntervalRef.current);
    }

    let pollCount = 0;
    const maxPolls = 60; // Poll for up to 5 minutes (60 * 5 seconds)
    const pollInterval = 5000; // Poll every 5 seconds

    backgroundPollIntervalRef.current = setInterval(async () => {
      pollCount++;
      
      if (pollCount > maxPolls) {
        // Stop polling after max attempts
        clearInterval(backgroundPollIntervalRef.current);
        backgroundPollIntervalRef.current = null;
        setBackgroundValidationMap(prev => {
          const updated = { ...prev };
          delete updated[instrument];
          return updated;
        });
        logger.warn('Stopped polling for validation - max attempts reached', { validationId, instrument });
        return;
      }

      try {
        await getValidation(auth, (data) => {
          if (data && !data.error) {
            // Check if validation is still in progress (completedAt is null)
            const isInProgress = !data.completedAt && data.startedAt;
            
            if (isInProgress) {
              // Validation still running - update progress if we have partial results
              if (data.ruleExecutions && data.ruleExecutions.length > 0) {
                // Update streaming gates with completed rules
                const completedGates = data.ruleExecutions.map(exec => ({
                  libraryRuleId: exec.libraryRuleId,
                  userRuleId: exec.userRuleId,
                  ruleName: exec.ruleName,
                  rule: exec.ruleName,
                  status: exec.status,
                  message: exec.message || '',
                  suggestedFix: exec.suggestedFix || null,
                  ruleType: exec.ruleType,
                  executionOrder: exec.executionOrder,
                  detailsJson: exec.detailsJson,
                  durationMs: exec.durationMs,
                }));
                
                setStreamingGatesMap(prev => ({
                  ...prev,
                  [instrument]: completedGates
                }));
                
                // Update progress
                if (data.totalRulesExecuted && data.totalRulesExecuted > 0) {
                  setStreamingProgressMap(prev => ({
                    ...prev,
                    [instrument]: { 
                      current: data.totalRulesExecuted, 
                      total: data.totalRulesExecuted + (data.rulesPassed || 0) + (data.rulesFailed || 0) + (data.rulesSkipped || 0)
                    }
                  }));
                }
              }
              // Continue polling - validation still in progress
              return;
            }
            
            // Validation completed - process the result
            logger.log('Validation result retrieved via polling:', data);
            
            // Clear background validation tracking
            setBackgroundValidationMap(prev => {
              const updated = { ...prev };
              delete updated[instrument];
              return updated;
            });
            
            // Stop polling
            if (backgroundPollIntervalRef.current) {
              clearInterval(backgroundPollIntervalRef.current);
              backgroundPollIntervalRef.current = null;
            }

            // Transform and store result
            const transformedResult = {
              ...data,
              status: data.status ? data.status.toLowerCase() : 'needs_validation',
              gates: data.ruleExecutions || [],
              validatedAt: data.completedAt || data.startedAt || Date.now(),
            };
            
            setValidationResultsMap(prev => ({
              ...prev,
              [instrument]: transformedResult
            }));
            setIsDirty(false);
            
            // Clear validating state
            if (validatingInstrumentRef.current === instrument) {
              setValidatingInstrument(null);
              validatingInstrumentRef.current = null;
            }

            // Refresh usage stats and subscription status
            getConceptGateUsageSummary(auth, (statsData) => {
              if (statsData && !statsData.error) {
                setUsageStats(statsData);
              }
            }, 7);

            getSubscriptionStatus(auth, (subData) => {
              if (subData && !subData.error) {
                setSubscriptionStatus(subData);
              }
            });
          } else if (data && data.error) {
            // Validation failed or not found
            logger.error('Validation error from polling:', data.error);
            setBackgroundValidationMap(prev => {
              const updated = { ...prev };
              delete updated[instrument];
              return updated;
            });
            if (backgroundPollIntervalRef.current) {
              clearInterval(backgroundPollIntervalRef.current);
              backgroundPollIntervalRef.current = null;
            }
            setError(data.error);
            if (validatingInstrumentRef.current === instrument) {
              setValidatingInstrument(null);
              validatingInstrumentRef.current = null;
            }
          }
          // If status is not set, validation is still in progress - continue polling
        }, validationId);
      } catch (error) {
        logger.error('Error polling for validation:', error);
        // Continue polling on error (might be temporary network issue)
      }
    }, pollInterval);
  }, [auth]);

  // Streaming validation - validates gate-by-gate with real-time updates
  const validateStreaming = useCallback(async () => {
    if (!auth?.currentUser || !currentTrade) return;
    if (isCodexUi) return;

    // Validate required fields
    if (!currentTrade.direction) {
      setError('Direction is required');
      return;
    }
    if (!currentTrade.entryPrice) {
      setError('Entry price is required');
      return;
    }
    if (!currentTrade.stopPrice) {
      setError('Stop price is required');
      return;
    }

    // Capture the instrument being validated
    const validationInstrument = (currentTrade.instrument || selectedInstrument || 'EURUSD').replace('_', '').toUpperCase();

    // Prepare trade spec
    const tradeSpec = {
      instrument: validationInstrument,
      direction: currentTrade.direction,
      entryPrice: typeof currentTrade.entryPrice === 'string'
        ? parseFloat(currentTrade.entryPrice) : currentTrade.entryPrice,
      stopPrice: typeof currentTrade.stopPrice === 'string'
        ? parseFloat(currentTrade.stopPrice) : currentTrade.stopPrice,
      targetPrice: currentTrade.targetPrice
        ? (typeof currentTrade.targetPrice === 'string'
          ? parseFloat(currentTrade.targetPrice) : currentTrade.targetPrice)
        : null,
      desiredRr: currentTrade.desiredRr
        ? (typeof currentTrade.desiredRr === 'string'
          ? parseFloat(currentTrade.desiredRr) : currentTrade.desiredRr)
        : null,
      riskPercent: currentTrade.riskPercent
        ? (typeof currentTrade.riskPercent === 'string'
          ? parseFloat(currentTrade.riskPercent)
          : currentTrade.riskPercent)
        : getStoredRiskPercent(),
      riskDollar: currentTrade.riskDollar
        ? (typeof currentTrade.riskDollar === 'string'
          ? parseFloat(currentTrade.riskDollar) : currentTrade.riskDollar)
        : null,
      // Include pipeline ID to validate only rules for the active pipeline
      pipelineId: activePipeline?.id || null,
    };

    // Track which instrument is being validated
    setValidatingInstrument(validationInstrument);
    validatingInstrumentRef.current = validationInstrument;

    // Initialize streaming state for this instrument
    setStreamingGatesMap(prev => ({ ...prev, [validationInstrument]: [] }));
    setStreamingProgressMap(prev => ({ ...prev, [validationInstrument]: { current: 0, total: 0 } }));
    setError(null);

    try {
      const idToken = await auth.currentUser.getIdToken(false);
      const url = getBaseApiUrl() + ENDPOINTS.CONCEPTGATE.VALIDATE_STREAM;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(tradeSpec),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      streamReaderRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      const collectedGates = [];
      const validationIdRef = { current: null };
      let receivedComplete = false;

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // Stream ended - check if we got a complete event
              const currentValidationId = validationIdRef.current;
              if (!receivedComplete && currentValidationId) {
                // Connection closed but validation may still be running
                logger.warn('Stream disconnected before completion, validation may continue in background', {
                  validationId: currentValidationId,
                  instrument: validationInstrument
                });
                // Start polling for results
                setBackgroundValidationMap(prev => ({
                  ...prev,
                  [validationInstrument]: {
                    validationId: currentValidationId,
                    startTime: Date.now()
                  }
                }));
                startPollingForValidation(currentValidationId, validationInstrument);
              }
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() === '') continue;

              const eventLines = line.split('\n');
              let eventName = 'message';
              let eventData = '';

              for (const eventLine of eventLines) {
                if (eventLine.startsWith('event:')) {
                  eventName = eventLine.substring(6).trim();
                } else if (eventLine.startsWith('data:')) {
                  eventData = eventLine.substring(5).trim();
                }
              }

              if (eventData) {
                try {
                  const data = JSON.parse(eventData);

                  switch (eventName) {
                    case 'start':
                      logger.log('Validation started:', data);
                      validationIdRef.current = data.validationId;
                      setStreamingProgressMap(prev => ({
                        ...prev,
                        [validationInstrument]: { current: 0, total: data.totalRules }
                      }));
                      break;

                    case 'gate':
                      logger.log('Gate executed:', data);
                      // Transform to frontend format
                      const gateResult = {
                        libraryRuleId: data.libraryRuleId,
                        userRuleId: data.userRuleId,
                        ruleName: data.ruleName,
                        rule: data.ruleName,
                        status: data.status,
                        message: data.message || '',
                        suggestedFix: data.suggestedFix || null,
                        ruleType: data.ruleType,
                        executionOrder: data.executionOrder,
                        detailsJson: data.detailsJson,
                        durationMs: data.durationMs,
                      };
                      collectedGates.push(gateResult);
                      setStreamingGatesMap(prev => ({
                        ...prev,
                        [validationInstrument]: [...collectedGates]
                      }));
                      setStreamingProgressMap(prev => ({
                        ...prev,
                        [validationInstrument]: { current: data.currentIndex, total: data.totalRules }
                      }));
                      break;

                    case 'complete':
                      logger.log('Validation complete:', data);
                      receivedComplete = true;
                      // Clear background validation tracking if it exists
                      setBackgroundValidationMap(prev => {
                        const updated = { ...prev };
                        delete updated[validationInstrument];
                        return updated;
                      });
                      // Stop polling if it was running
                      if (backgroundPollIntervalRef.current) {
                        clearInterval(backgroundPollIntervalRef.current);
                        backgroundPollIntervalRef.current = null;
                      }
                      
                      const transformedResult = {
                        ...data,
                        status: data.status ? data.status.toLowerCase() : 'needs_validation',
                        gates: collectedGates,
                        // Map backend field to UI expected field for display
                        validatedAt: data.completedAt || data.startedAt || Date.now(),
                      };
                      // Store result in the instrument-keyed map
                      setValidationResultsMap(prev => ({
                        ...prev,
                        [validationInstrument]: transformedResult
                      }));
                      setIsDirty(false);
                      // Clear validating state only if this is still the active validation
                      if (validatingInstrumentRef.current === validationInstrument) {
                        setValidatingInstrument(null);
                        validatingInstrumentRef.current = null;
                      }

                      // Refresh usage stats
                      getConceptGateUsageSummary(auth, (statsData) => {
                        if (statsData && !statsData.error) {
                          setUsageStats(statsData);
                        }
                      }, 7);

                      // Refresh subscription status after validation
                      getSubscriptionStatus(auth, (subData) => {
                        if (subData && !subData.error) {
                          setSubscriptionStatus(subData);
                        }
                      });
                      break;

                    case 'error':
                      logger.error('Validation error:', data);
                      setError(data.message || data.error);
                      // Clear validating state
                      if (validatingInstrumentRef.current === validationInstrument) {
                        setValidatingInstrument(null);
                        validatingInstrumentRef.current = null;
                      }
                      // If it's a limit error, refresh subscription status
                      if (data.usage) {
                        setSubscriptionStatus(data.usage);
                      } else {
                        // Refresh subscription status anyway to get latest
                        getSubscriptionStatus(auth, (subData) => {
                          if (subData && !subData.error) {
                            setSubscriptionStatus(subData);
                          }
                        });
                      }
                      break;

                    default:
                      logger.log('Unknown event:', eventName, data);
                  }
                } catch (parseError) {
                  logger.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        } catch (streamError) {
          logger.error('Error processing validation stream:', streamError);
          // If we have a validationId, validation may still be running
          const currentValidationId = validationIdRef.current;
          if (currentValidationId && !receivedComplete) {
            logger.warn('Stream error but validation may continue', {
              validationId: currentValidationId,
              error: streamError.message
            });
            setBackgroundValidationMap(prev => ({
              ...prev,
              [validationInstrument]: {
                validationId: currentValidationId,
                startTime: Date.now()
              }
            }));
            startPollingForValidation(currentValidationId, validationInstrument);
          } else {
            setError('Error processing validation stream');
            if (validatingInstrumentRef.current === validationInstrument) {
              setValidatingInstrument(null);
              validatingInstrumentRef.current = null;
            }
          }
        }
      };

      processStream();

    } catch (err) {
      logger.error('Streaming validation error:', err);
      setError('Validation failed: ' + (err.message || 'Unknown error'));
      if (validatingInstrumentRef.current === validationInstrument) {
        setValidatingInstrument(null);
        validatingInstrumentRef.current = null;
      }
    }
  }, [auth, currentTrade, selectedInstrument, activePipeline?.id, startPollingForValidation]);

  // Cancel streaming validation
  const cancelValidation = useCallback(() => {
    if (streamReaderRef.current) {
      streamReaderRef.current.cancel();
      streamReaderRef.current = null;
    }
    // Stop background polling
    if (backgroundPollIntervalRef.current) {
      clearInterval(backgroundPollIntervalRef.current);
      backgroundPollIntervalRef.current = null;
    }
    // Clear background validation tracking
    setBackgroundValidationMap({});
    setValidatingInstrument(null);
    validatingInstrumentRef.current = null;
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (backgroundPollIntervalRef.current) {
        clearInterval(backgroundPollIntervalRef.current);
      }
    };
  }, []);

  // Clear trade
  const clearTrade = useCallback(async () => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    const instrumentToDelete = (currentTrade?.instrument || selectedInstrument || '').replace('_', '').toUpperCase();
    if (instrumentToDelete) {
      await deleteConceptGateTrade(auth, (result) => {
        if (result && !result.error) {
          setCurrentTrade(null);
          // Clear validation result for this instrument
          setValidationResultsMap(prev => {
            const newMap = { ...prev };
            delete newMap[instrumentToDelete];
            return newMap;
          });
          setIsDirty(false);
          // Clear streaming state for this instrument
          setStreamingGatesMap(prev => {
            const newMap = { ...prev };
            delete newMap[instrumentToDelete];
            return newMap;
          });
        }
      }, instrumentToDelete);
    } else {
      // No instrument, just clear local state
      setCurrentTrade(null);
      setIsDirty(false);
    }
  }, [auth, currentTrade, selectedInstrument]);

  // Refresh trade data from server (called when trade is saved from chat)
  const refreshTrade = useCallback((instrument) => {
    const targetInstrument = normalizeInstrument(instrument || selectedInstrument);
    if (!targetInstrument || !auth?.currentUser) return;

    getConceptGateTrade(auth, (trade) => {
      if (trade) {
        setCurrentTrade({
          ...trade,
          instrument: trade.instrument || targetInstrument,
        });

        // Restore validation result if available - store in the map
        if (trade.latestValidation && trade.latestValidation.status) {
          const validation = trade.latestValidation;
          const restoredResult = {
            status: validation.status.toLowerCase(),
            totalDurationMs: validation.totalDurationMs,
            totalTokens: validation.totalTokens,
            estimatedCostUsd: validation.estimatedCostUsd,
            gates: (validation.ruleExecutions || []).map((execution) => {
              let actualValue = null;
              let thresholdValue = null;
              if (execution.detailsJson) {
                try {
                  const details = typeof execution.detailsJson === 'string'
                    ? JSON.parse(execution.detailsJson)
                    : execution.detailsJson;
                  actualValue = details.actualValue || details.actual_value;
                  thresholdValue = details.thresholdValue || details.threshold_value;
                } catch (e) {
                  // Ignore parse error
                }
              }
              return {
                id: execution.id,
                libraryRuleId: execution.libraryRuleId,
                userRuleId: execution.userRuleId,
                ruleName: execution.ruleName,
                rule: execution.ruleName,
                ruleType: execution.ruleType,
                status: execution.status || 'SKIPPED',
                message: execution.message,
                suggestedFix: execution.suggestedFix,
                actualValue,
                thresholdValue,
                detailsJson: execution.detailsJson,
                executionOrder: execution.executionOrder,
                durationMs: execution.durationMs,
              };
            }),
            validationId: validation.id,
            validatedAt: validation.completedAt || validation.startedAt,
          };
          // Store in the instrument-keyed map
          setValidationResultsMap(prev => ({
            ...prev,
            [targetInstrument]: restoredResult
          }));
          setIsDirty(false);
        } else {
          setIsDirty(false);
        }
      }
    }, targetInstrument);
  }, [auth, selectedInstrument, normalizeInstrument]);

  // ==================== Rule Config Modal ====================

  // Open config modal for a rule
  const openConfigModal = useCallback((userRule, libraryRule) => {
    setSelectedRuleForConfig({ userRule, libraryRule });
    setConfigModalOpen(true);
    setTestResult(null);
  }, []);

  // Close config modal
  const closeConfigModal = useCallback(() => {
    setConfigModalOpen(false);
    setSelectedRuleForConfig(null);
    setTestResult(null);
  }, []);

  // Test a single rule - ensures trade is saved first
  const testRule = useCallback(async (userRuleId) => {
    if (!auth?.currentUser || !currentTrade) {
      setTestResult({ error: 'No trade to test against' });
      return;
    }
    if (isCodexUi) {
      setTestResult({ error: 'Rule testing disabled in Codex mode' });
      return;
    }

    setTestLoading(true);
    setTestResult(null);

    // Prepare trade spec for saving
    const tradeSpec = {
      instrument: (currentTrade.instrument || selectedInstrument || 'EURUSD').replace('_', '').toUpperCase(),
      direction: currentTrade.direction,
      entryPrice: typeof currentTrade.entryPrice === 'string'
        ? parseFloat(currentTrade.entryPrice) : currentTrade.entryPrice,
      stopPrice: typeof currentTrade.stopPrice === 'string'
        ? parseFloat(currentTrade.stopPrice) : currentTrade.stopPrice,
      targetPrice: currentTrade.targetPrice
        ? (typeof currentTrade.targetPrice === 'string'
          ? parseFloat(currentTrade.targetPrice) : currentTrade.targetPrice)
        : null,
    };

    // Save trade first to ensure backend has it
    saveConceptGateTrade(auth, (saveResult) => {
      if (saveResult && !saveResult.error) {
        // Now run the test
        testUserRule(auth, (result) => {
          if (result && !result.error) {
            setTestResult(result);
          } else {
            setTestResult({ error: result?.error || 'Test failed' });
          }
          setTestLoading(false);
        }, userRuleId);
      } else {
        setTestResult({ error: 'Failed to save trade before testing' });
        setTestLoading(false);
      }
    }, tradeSpec);
  }, [auth, currentTrade, selectedInstrument]);

  // Load user rules for the active pipeline
  const loadUserRules = useCallback((pipelineIdOverride = null) => {
    if (!auth?.currentUser || !selectedInstrument) {
      setUserRules([]);
      return;
    }

    // Use the provided pipeline ID, or fall back to the active pipeline
    const pipelineId = pipelineIdOverride || activePipeline?.id;
    const normalizedInst = selectedInstrument.replace('_', '').toUpperCase();

    if (pipelineId) {
      // Load rules for the specific pipeline
      console.log('[ConceptGateContext] loadUserRules called, pipelineId:', pipelineId);
      apiGetPipelineRules(auth, (result) => {
        console.log('[ConceptGateContext] loadUserRules (pipeline) result:', result?.length || 0, 'rules');
        if (result && !result.error) {
          setUserRules(result);
        }
      }, pipelineId);
    } else {
      // Fallback: load rules by instrument (legacy behavior)
      console.log('[ConceptGateContext] loadUserRules called, instrument:', normalizedInst, '(no active pipeline)');
      getUserRules(auth, (result) => {
        console.log('[ConceptGateContext] loadUserRules result:', result?.length || 0, 'rules');
        if (result && !result.error) {
          setUserRules(result);
        }
      }, { instrument: normalizedInst });
    }
  }, [auth, selectedInstrument, activePipeline?.id]);

  useEffect(() => {
    loadUserRules();
  }, [loadUserRules]);

  // Update rule configuration
  const updateRuleConfig = useCallback(async (userRuleId, customConfig) => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    return new Promise((resolve) => {
      updateUserRuleConfig(auth, (result) => {
        if (result && !result.error) {
          // Refresh user rules using the proper method that respects active pipeline
          loadUserRules();
          // Mark as dirty if we have a trade
          if (currentTrade) {
            setIsDirty(true);
          }
          resolve(result);
        } else {
          resolve(null);
        }
      }, userRuleId, customConfig);
    });
  }, [auth, loadUserRules, currentTrade]);

  // ==================== Pipeline Management ====================

  // Load pipelines for the current instrument
  const loadPipelines = useCallback(async () => {
    if (!auth?.currentUser || !selectedInstrument) {
      setPipelines([]);
      setActivePipeline(null);
      return;
    }

    const normalizedInst = selectedInstrument.replace('_', '').toUpperCase();
    setPipelineLoading(true);

    try {
      // Load all pipelines for this instrument
      await apiGetPipelines(auth, (result) => {
        if (result && !result.error) {
          setPipelines(result);
          // Find and set the active pipeline
          const active = result.find(p => p.isActive || p.active);
          if (active) {
            setActivePipeline(active);
          }
        } else {
          logger.error('Failed to load pipelines:', result?.error);
        }
      }, normalizedInst);

      // If no pipelines exist, get/create the active one (backend creates default)
      if (pipelines.length === 0) {
        await apiGetActivePipeline(auth, (result) => {
          if (result && !result.error) {
            setActivePipeline(result);
            setPipelines([result]);
          }
        }, normalizedInst);
      }
    } finally {
      setPipelineLoading(false);
    }
  }, [auth, selectedInstrument, pipelines.length]);

  // Load pipelines when instrument changes or auth becomes ready
  useEffect(() => {
    if (selectedInstrument && auth?.currentUser) {
      loadPipelines();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstrument, auth?.currentUser]);

  // Create a new pipeline
  const createNewPipeline = useCallback(async (name, description = '') => {
    if (!auth?.currentUser || !selectedInstrument) return null;
    if (isCodexUi) return null;

    const normalizedInst = selectedInstrument.replace('_', '').toUpperCase();

    return new Promise((resolve) => {
      apiCreatePipeline(auth, (result) => {
        if (result && !result.error) {
          // Add to local state
          setPipelines(prev => [...prev, result]);
          resolve(result);
        } else {
          logger.error('Failed to create pipeline:', result?.error);
          resolve(null);
        }
      }, { instrument: normalizedInst, name, description });
    });
  }, [auth, selectedInstrument]);

  // Update pipeline name/description
  const updatePipelineInfo = useCallback(async (pipelineId, { name, description }) => {
    if (!auth?.currentUser) return null;
    if (isCodexUi) return null;

    return new Promise((resolve) => {
      apiUpdatePipeline(auth, (result) => {
        if (result && !result.error) {
          // Update local state
          setPipelines(prev => prev.map(p =>
            p.id === pipelineId ? { ...p, name, description } : p
          ));
          // Update active pipeline if it's the one being updated
          setActivePipeline(prev =>
            prev?.id === pipelineId ? { ...prev, name, description } : prev
          );
          resolve(result);
        } else {
          logger.error('Failed to update pipeline:', result?.error);
          resolve(null);
        }
      }, pipelineId, { name, description });
    });
  }, [auth]);

  // Switch to a different pipeline (make it active)
  const switchPipeline = useCallback(async (pipelineId) => {
    if (!auth?.currentUser || !selectedInstrument) return;
    if (isCodexUi) return;

    const normalizedInst = selectedInstrument.replace('_', '').toUpperCase();
    const targetPipeline = pipelines.find(p => p.id === pipelineId);
    if (!targetPipeline) return;

    setPipelineLoading(true);

    await apiSetActivePipeline(auth, (result) => {
      if (result && !result.error) {
        // Update local state - mark new pipeline as active, others as inactive
        setPipelines(prev => prev.map(p => ({
          ...p,
          isActive: p.id === pipelineId,
          active: p.id === pipelineId,
        })));
        setActivePipeline({ ...targetPipeline, isActive: true, active: true });

        // Clear current validation results since we're switching pipelines
        setValidationResultsMap(prev => {
          const newMap = { ...prev };
          delete newMap[normalizedInst];
          return newMap;
        });
        setIsDirty(true);

        // Reload rules for the NEW pipeline (pass pipelineId explicitly since activePipeline state hasn't updated yet)
        loadUserRules(pipelineId);
      } else {
        logger.error('Failed to switch pipeline:', result?.error);
      }
      setPipelineLoading(false);
    }, pipelineId, normalizedInst);
  }, [auth, selectedInstrument, pipelines, loadUserRules]);

  // Delete a pipeline
  const deletePipelineById = useCallback(async (pipelineId) => {
    if (!auth?.currentUser) return false;
    if (isCodexUi) return false;

    // Prevent deleting the active pipeline
    if (activePipeline?.id === pipelineId) {
      logger.warn('Cannot delete the active pipeline');
      return false;
    }

    return new Promise((resolve) => {
      apiDeletePipeline(auth, (result) => {
        if (result && !result.error) {
          // Remove from local state
          setPipelines(prev => prev.filter(p => p.id !== pipelineId));
          resolve(true);
        } else {
          logger.error('Failed to delete pipeline:', result?.error);
          resolve(false);
        }
      }, pipelineId);
    });
  }, [auth, activePipeline]);

  // Duplicate a pipeline with all its rules
  const duplicatePipelineById = useCallback(async (pipelineId, newName) => {
    if (!auth?.currentUser) return null;
    if (isCodexUi) return null;

    return new Promise((resolve) => {
      apiDuplicatePipeline(auth, (result) => {
        if (result && !result.error) {
          // Add the new pipeline to local state
          setPipelines(prev => [...prev, result]);
          resolve(result);
        } else {
          logger.error('Failed to duplicate pipeline:', result?.error);
          resolve(null);
        }
      }, pipelineId, newName);
    });
  }, [auth]);

  // ==================== Rules Management ====================

  const openRulesManagement = useCallback(() => {
    setRulesManagementOpen(true);
  }, []);

  const closeRulesManagement = useCallback(() => {
    setRulesManagementOpen(false);
  }, []);

  // Toggle user rule enabled status
  const toggleUserRuleEnabled = useCallback(async (userRuleId, currentEnabled) => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    await apiUpdateUserRuleEnabled(auth, (result) => {
      if (result && !result.error) {
        // Update local state
        setUserRules(prev => prev.map(rule =>
          rule.id === userRuleId ? { ...rule, enabled: !currentEnabled } : rule
        ));
        // Mark as dirty if we have a trade
        if (currentTrade) {
          setIsDirty(true);
        }
      }
    }, userRuleId, !currentEnabled);
  }, [auth, currentTrade]);

  // Dismiss a specific undo item from queue
  const dismissUndoItem = useCallback((ruleId) => {
    // Clear the timeout for this specific rule
    const timeoutId = undoTimeoutRefs.current.get(ruleId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      undoTimeoutRefs.current.delete(ruleId);
    }
    // Remove from queue
    setUndoQueue(prev => prev.filter(item => item.ruleId !== ruleId));
  }, []);

  // Dismiss all undo toasts (legacy compatibility)
  const dismissUndoToast = useCallback(() => {
    // Clear all timeouts
    undoTimeoutRefs.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    undoTimeoutRefs.current.clear();
    setUndoQueue([]);
  }, []);

  // Remove user rule with undo support (queue-based)
  const removeUserRule = useCallback(async (userRuleId, ruleName) => {
    if (!auth?.currentUser) return;
    if (isCodexUi) return;

    // Store the rule for undo
    const removedRule = userRules.find(r => r.id === userRuleId);
    if (!removedRule) return;

    // Optimistically remove from UI
    setUserRules(prev => prev.filter(rule => rule.id !== userRuleId));

    // Add to undo queue
    const undoItem = {
      ruleId: userRuleId,
      message: `"${ruleName || removedRule.name || 'Rule'}" removed`,
      removedRule,
    };
    setUndoQueue(prev => [...prev, undoItem]);

    // Set timeout to actually delete after 5 seconds
    const timeoutId = setTimeout(async () => {
      // Actually delete from server
      await apiDeleteUserRule(auth, (result) => {
        if (!result || result.error) {
          // Restore rule if delete failed
          setUserRules(prev => [...prev, removedRule].sort(
            (a, b) => (a.executionOrder || 0) - (b.executionOrder || 0)
          ));
          logger.error('Failed to delete rule:', result?.error);
        }
      }, userRuleId);

      // Remove from queue after deletion
      setUndoQueue(prev => prev.filter(item => item.ruleId !== userRuleId));
      undoTimeoutRefs.current.delete(userRuleId);
    }, 5000);

    undoTimeoutRefs.current.set(userRuleId, timeoutId);

    // Mark as dirty if we have a trade
    if (currentTrade) {
      setIsDirty(true);
    }
  }, [auth, currentTrade, userRules]);

  // Undo rule removal
  // Undo a specific rule removal
  const undoRuleRemovalById = useCallback((ruleId) => {
    const undoItem = undoQueue.find(item => item.ruleId === ruleId);
    if (!undoItem?.removedRule) return;

    // Restore the rule to local state
    setUserRules(prev => [...prev, undoItem.removedRule].sort(
      (a, b) => (a.executionOrder || 0) - (b.executionOrder || 0)
    ));

    // Clear the timeout so we don't delete from server
    const timeoutId = undoTimeoutRefs.current.get(ruleId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      undoTimeoutRefs.current.delete(ruleId);
    }

    // Remove from queue
    setUndoQueue(prev => prev.filter(item => item.ruleId !== ruleId));
  }, [undoQueue]);

  // Legacy: undo the most recent rule removal (for backward compatibility)
  const undoRuleRemoval = useCallback(() => {
    if (undoQueue.length === 0) return;
    // Undo the most recent (last) item in the queue
    const lastItem = undoQueue[undoQueue.length - 1];
    undoRuleRemovalById(lastItem.ruleId);
  }, [undoQueue, undoRuleRemovalById]);

  // Toggle panel visibility
  const togglePanel = useCallback(() => {
    setPanelVisible((prev) => !prev);
  }, []);

  // Get validation status for display
  const getStatus = useCallback(() => {
    if (!currentTrade) return 'empty';
    if (validating) return 'validating';
    if (isDirty) return 'stale';
    if (lastResult && lastResult.status) {
      // Status is already transformed to lowercase in validate callback
      return lastResult.status;
    }
    return 'needs_validation';
  }, [currentTrade, validating, isDirty, lastResult]);

  const value = {
    // Account
    account,
    updateAccount,

    // Settings
    settings,
    updateSettings,
    resetSettings,

    // Usage stats
    usageStats,

    // Subscription status
    subscriptionStatus,
    refreshSubscription,

    // Instrument
    selectedInstrument,
    selectedCategory,
    setSelectedInstrument,

    // Trade
    currentTrade,
    setTradeSpec,
    updateTradeField,
    clearTrade,
    refreshTrade,

    // Validation
    lastResult,
    validate,
    validateStreaming,
    cancelValidation,
    validating,
    streamingGates,
    streamingProgress,
    backgroundValidationMap, // Track validations continuing in background after disconnect
    useStreamingValidation,
    setUseStreamingValidation,
    isDirty,
    getStatus,

    // UI
    panelVisible,
    setPanelVisible,
    togglePanel,
    panelWidth,
    setPanelWidth,
    editMode,
    setEditMode,
    loading,
    error,

    // Rule config modal
    configModalOpen,
    selectedRuleForConfig,
    openConfigModal,
    closeConfigModal,
    updateRuleConfig,
    testRule,
    testResult,
    testLoading,

    // User rules
    userRules,
    loadUserRules,

    // Pipelines
    pipelines,
    activePipeline,
    pipelineLoading,
    loadPipelines,
    createNewPipeline,
    updatePipelineInfo,
    switchPipeline,
    deletePipelineById,
    duplicatePipelineById,

    // Rules management modal
    rulesManagementOpen,
    openRulesManagement,
    closeRulesManagement,
    toggleUserRuleEnabled,
    removeUserRule,
    undoQueue,
    undoToast: undoQueue.length > 0 ? undoQueue[undoQueue.length - 1] : null, // Legacy compatibility
    undoRuleRemoval,
    undoRuleRemovalById,
    dismissUndoToast,
    dismissUndoItem,
    resetConceptGateState,
  };

  return (
    <ConceptGateContext.Provider value={value}>
      {children}
    </ConceptGateContext.Provider>
  );
};

export const useConceptGate = () => {
  const context = useContext(ConceptGateContext);
  if (context === undefined) {
    throw new Error('useConceptGate must be used within a ConceptGateProvider');
  }
  return context;
};
